import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, GraduationCap, LockKeyhole, RotateCcw, Sparkles } from "lucide-react";
import { STATUS_ICON_SIZE } from "@/constants/ui";
import { api, formatApiErrorDetail } from "@/services/api";

function lessonProgressFor(moduleProgress, lessonId) {
  return moduleProgress?.lessons?.find((item) => item.lesson_id === lessonId) || { status: "not_started", best_score: 0, attempts: 0 };
}

function renderLessonIcon(completed, locked) {
  if (completed) return <CheckCircle2 size={20} />;
  if (locked) return <LockKeyhole size={20} />;
  return <Circle size={20} />;
}

function getLessonProgressText(progress) {
  if (progress.locked) return "bloqueada";
  if (progress.review_required) return "revise antes de tentar";
  return `${progress.best_score}% melhor · ${progress.attempts} tentativas`;
}

function getSubmitLabel({ pending, progress }) {
  if (pending) return "Corrigindo...";
  if (progress?.review_required) return "Tentar após revisão";
  return "Corrigir exercícios";
}

function ModuleCard({ item, activeModuleId, onSelect }) {
  const { module, progress } = item;
  const isActive = activeModuleId === module.id;
  return (
    <button
      type="button"
      className={`grammar-module-card ${isActive ? "grammar-module-card-active" : ""}`}
      onClick={() => onSelect(module.id)}
      data-testid={`grammar-module-card-${module.id}`}
    >
      <span className="grammar-module-order" data-testid={`grammar-module-order-${module.id}`}>{module.order}</span>
      <span className="label-chip bg-white" data-testid={`grammar-module-level-${module.id}`}>{module.level}</span>
      <strong data-testid={`grammar-module-title-${module.id}`}>{module.title}</strong>
      <small data-testid={`grammar-module-description-${module.id}`}>{module.description_pt}</small>
      <span className="grammar-progress-track" data-testid={`grammar-module-progress-track-${module.id}`}>
        <span style={{ width: `${progress.percent_complete}%` }} data-testid={`grammar-module-progress-fill-${module.id}`} />
      </span>
      <em data-testid={`grammar-module-progress-text-${module.id}`}>{progress.completed_lessons}/{progress.total_lessons} aulas · {progress.percent_complete}%</em>
    </button>
  );
}

function LessonList({ moduleItem, selectedLessonId, onSelectLesson }) {
  if (!moduleItem) return null;
  return (
    <aside className="grammar-lesson-list" data-testid="grammar-lesson-list">
      <h2 className="font-display text-3xl font-black" data-testid="grammar-lessons-heading">Aulas do módulo</h2>
      {moduleItem.module.lessons.map((lesson, index) => {
        const progress = lessonProgressFor(moduleItem.progress, lesson.id);
        const completed = progress.status === "completed";
        const locked = progress.locked;
        const active = selectedLessonId === lesson.id;
        return (
          <button
            key={lesson.id}
            type="button"
            className={`grammar-lesson-button ${active ? "grammar-lesson-button-active" : ""}`}
            onClick={() => !locked && onSelectLesson(lesson.id)}
            disabled={locked}
            data-testid={`grammar-lesson-button-${lesson.id}`}
          >
            {renderLessonIcon(completed, locked)}
            <span>
              <strong data-testid={`grammar-lesson-title-${lesson.id}`}>{index + 1}. {lesson.title}</strong>
              <small data-testid={`grammar-lesson-progress-${lesson.id}`}>{getLessonProgressText(progress)}</small>
            </span>
          </button>
        );
      })}
    </aside>
  );
}

function ChoiceExercise({ exercise, value, onAnswer }) {
  return (
    <div className="grammar-options" data-testid={`grammar-exercise-options-${exercise.id}`}>
      {exercise.options.map((option) => (
        <button key={option} type="button" className={`grammar-option-button ${value === option ? "grammar-option-selected" : ""}`} onClick={() => onAnswer(exercise.id, option)} data-testid={`grammar-option-${exercise.id}-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{option}</button>
      ))}
    </div>
  );
}

function OrderExercise({ exercise, value, onAnswer }) {
  const addWord = (option) => onAnswer(exercise.id, value ? `${value} ${option}` : option);
  return (
    <>
      <div className="grammar-options" data-testid={`grammar-exercise-options-${exercise.id}`}>
        {exercise.options.map((option) => (
          <button key={option} type="button" className="grammar-option-button" onClick={() => addWord(option)} data-testid={`grammar-option-${exercise.id}-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{option}</button>
        ))}
        {value && <button type="button" className="grammar-option-button" onClick={() => onAnswer(exercise.id, "")} data-testid={`grammar-order-clear-${exercise.id}`}>Limpar</button>}
      </div>
      {value && <p className="mt-3 text-sm font-black text-slate-700" data-testid={`grammar-order-current-${exercise.id}`}>Sua frase: {value}</p>}
    </>
  );
}

function TextExercise({ exercise, value, onAnswer }) {
  const isSpeaking = exercise.type === "speaking";
  return (
    <>
      {isSpeaking && exercise.speaking_prompt_pt && <div className="grammar-speaking-prompt" data-testid={`grammar-speaking-prompt-${exercise.id}`}>{exercise.speaking_prompt_pt}</div>}
      <input className="auth-input mt-4" value={value || ""} onChange={(event) => onAnswer(exercise.id, event.target.value)} placeholder={isSpeaking ? "Digite a frase que você falou" : "Digite sua resposta"} data-testid={`grammar-answer-input-${exercise.id}`} />
    </>
  );
}

function ExerciseInput({ exercise, value, onAnswer }) {
  if (exercise.type === "choice") return <ChoiceExercise exercise={exercise} value={value} onAnswer={onAnswer} />;
  if (exercise.type === "order") return <OrderExercise exercise={exercise} value={value} onAnswer={onAnswer} />;
  return <TextExercise exercise={exercise} value={value} onAnswer={onAnswer} />;
}

function ExerciseBlock({ exercise, value, onAnswer, result }) {
  return (
    <article className="grammar-exercise" data-testid={`grammar-exercise-${exercise.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="zero-label" data-testid={`grammar-exercise-prompt-${exercise.id}`}>{exercise.prompt_pt}</p>
          <h3 className="mt-2 font-display text-2xl font-black" data-testid={`grammar-exercise-question-${exercise.id}`}>{exercise.question}</h3>
        </div>
        {result && <span className={`grammar-result-pill ${result.correct ? "grammar-result-correct" : "grammar-result-wrong"}`} data-testid={`grammar-exercise-result-${exercise.id}`}>{result.correct ? "certo" : "rever"}</span>}
      </div>
      <ExerciseInput exercise={exercise} value={value} onAnswer={onAnswer} />
      {result && <p className="mt-3 text-sm font-black text-slate-600" data-testid={`grammar-exercise-explanation-${exercise.id}`}>Resposta: {result.expected_answer}. {result.explanation_pt}</p>}
    </article>
  );
}

function LessonPanel({ lesson, answers, onAnswer, onReview, onSubmit, attemptResult, pending, error, progress }) {
  if (!lesson) {
    return <section className="grammar-lesson-panel" data-testid="grammar-lesson-empty-state">Carregando aula...</section>;
  }
  const resultById = Object.fromEntries((attemptResult?.results || []).map((item) => [item.exercise_id, item]));
  const allAnswered = lesson.exercises.every((exercise) => (answers[exercise.id] || "").trim());
  return (
    <section className="grammar-lesson-panel" data-testid="grammar-lesson-panel">
      <div className="grammar-lesson-hero" data-testid="grammar-lesson-hero">
        <span className="section-kicker" data-testid="grammar-lesson-level">{lesson.level}</span>
        <h2 className="font-display text-4xl font-black sm:text-5xl" data-testid="grammar-active-lesson-title">{lesson.title}</h2>
        <p className="text-base font-bold text-slate-700 md:text-lg" data-testid="grammar-active-lesson-objective">{lesson.objective_pt}</p>
      </div>
      <article className="grammar-explanation" data-testid="grammar-explanation-card">
        <p data-testid="grammar-explanation-text">{lesson.explanation_pt}</p>
        <div className="practice-phrases mt-4" data-testid="grammar-example-list">
          {lesson.examples.map((example) => <span key={example} data-testid={`grammar-example-${example.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{example}</span>)}
        </div>
      </article>
      <div className="next-step-box" data-testid="grammar-next-step-box">Próximo passo: faça os exercícios abaixo e clique em “Corrigir exercícios”.</div>
      <div className="grid gap-4" data-testid="grammar-exercise-list">
        {lesson.exercises.map((exercise) => (
          <ExerciseBlock key={exercise.id} exercise={exercise} value={answers[exercise.id]} onAnswer={onAnswer} result={resultById[exercise.id]} />
        ))}
      </div>
      {attemptResult && (
        <div className="grammar-score-panel" data-testid="grammar-score-panel">
          <Sparkles size={STATUS_ICON_SIZE} />
          <div>
            <strong data-testid="grammar-score-text">{attemptResult.score}% · {attemptResult.correct_count}/{attemptResult.total} corretas</strong>
            <p data-testid="grammar-pass-text">{attemptResult.passed ? "Aula concluída. Próxima liberada na trilha." : "Revise as explicações antes de tentar novamente."}</p>
          </div>
        </div>
      )}
      {error && <div className="auth-error" role="alert" data-testid="grammar-submit-error-alert">{error}</div>}
      <div className="flex flex-wrap gap-3" data-testid="grammar-submit-controls">
        <button type="button" className="primary-action w-auto" disabled={!allAnswered || pending || progress?.status === "completed"} onClick={onSubmit} data-testid="grammar-submit-attempt-button">
          {getSubmitLabel({ pending, progress })} <GraduationCap size={18} />
        </button>
        {progress?.review_required && <button type="button" className="secondary-action w-auto" onClick={onReview} data-testid="grammar-review-confirm-button"><RotateCcw size={18} /> Revisei, liberar tentativa</button>}
      </div>
    </section>
  );
}


function useGrammarLessonState({ activeLessonId }) {
  const [lessonState, setLessonState] = useState({});
  const saveAttemptResult = (result, fallbackAnswers) => setLessonState((current) => ({
    ...current,
    [result.lesson_id]: {
      answers: current[result.lesson_id]?.answers || fallbackAnswers,
      attemptResult: result,
      error: "",
      reviewConfirmed: false,
    },
  }));
  const saveAttemptError = (err, currentLessonState) => setLessonState((current) => ({
    ...current,
    [activeLessonId]: {
      answers: currentLessonState.answers,
      attemptResult: currentLessonState.attemptResult,
      error: formatApiErrorDetail(err.response?.data?.detail),
      reviewConfirmed: currentLessonState.reviewConfirmed || false,
    },
  }));
  const handleAnswer = (exerciseId, answer) => setLessonState((current) => ({
    ...current,
    [activeLessonId]: {
      answers: { ...(current[activeLessonId]?.answers || {}), [exerciseId]: answer },
      attemptResult: null,
      error: "",
      reviewConfirmed: current[activeLessonId]?.reviewConfirmed || false,
    },
  }));
  const confirmReview = () => setLessonState((current) => ({
    ...current,
    [activeLessonId]: {
      ...(current[activeLessonId] || { answers: {} }),
      reviewConfirmed: true,
      error: "Revisão confirmada. Agora tente novamente.",
    },
  }));
  return { confirmReview, handleAnswer, lessonState, saveAttemptError, saveAttemptResult };
}

export default function Grammar() {
  const queryClient = useQueryClient();
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const { data: modules = [], isLoading } = useQuery({ queryKey: ["grammar-modules"], queryFn: api.getGrammarModules });

  const activeModuleId = selectedModuleId || modules[0]?.module.id || "";
  const selectedModule = modules.find((item) => item.module.id === activeModuleId);
  const activeLessonId = selectedLessonId || selectedModule?.module.lessons[0]?.id || "";
  const selectedLesson = selectedModule?.module.lessons.find((lesson) => lesson.id === activeLessonId);
  const selectedProgress = selectedModule ? lessonProgressFor(selectedModule.progress, activeLessonId) : null;
  const { confirmReview, handleAnswer, lessonState, saveAttemptError, saveAttemptResult } = useGrammarLessonState({ activeLessonId });
  const currentLessonState = lessonState[activeLessonId] || { answers: {}, attemptResult: null, error: "" };

  const mutation = useMutation({
    mutationFn: () => api.submitGrammarAttempt(selectedLesson.id, Object.entries(currentLessonState.answers).map(([exercise_id, answer]) => ({ exercise_id, answer })), Boolean(currentLessonState.reviewConfirmed)),
    onSuccess: (result) => {
      saveAttemptResult(result, currentLessonState.answers);
      queryClient.invalidateQueries({ queryKey: ["grammar-modules"] });
    },
    onError: (err) => saveAttemptError(err, currentLessonState),
  });

  const handleModuleSelect = (moduleId) => {
    const moduleItem = modules.find((item) => item.module.id === moduleId);
    setSelectedModuleId(moduleId);
    setSelectedLessonId(moduleItem?.module.lessons[0]?.id || "");
  };
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="grammar-page">
      <section className="page-title-row" data-testid="grammar-title-section">
        <p className="section-kicker" data-testid="grammar-page-kicker">trilha de gramática</p>
        <h1 className="font-display text-4xl font-black sm:text-5xl lg:text-6xl" data-testid="grammar-page-heading">Do básico ao avançado.</h1>
        <p className="max-w-3xl text-base font-semibold text-slate-600 md:text-lg" data-testid="grammar-page-description">
          Escolha uma aula. Os exercícios aparecem no centro da página. Responda e clique em Corrigir.
        </p>
      </section>

      {isLoading ? (
        <div className="neo-card surface-neutral mt-8 p-6" data-testid="grammar-loading-state">Carregando trilha...</div>
      ) : (
        <>
          <section className="grammar-module-grid" data-testid="grammar-module-grid">
            {modules.map((item) => <ModuleCard key={item.module.id} item={item} activeModuleId={selectedModule?.module.id} onSelect={handleModuleSelect} />)}
          </section>
          <section className="grammar-workspace" data-testid="grammar-workspace">
            <LessonList moduleItem={selectedModule} selectedLessonId={selectedLesson?.id} onSelectLesson={setSelectedLessonId} />
            <LessonPanel
              lesson={selectedLesson}
              answers={currentLessonState.answers}
              onAnswer={handleAnswer}
              onReview={confirmReview}
              onSubmit={() => mutation.mutate()}
              attemptResult={currentLessonState.attemptResult}
              pending={mutation.isPending}
              error={currentLessonState.error}
              progress={selectedProgress}
            />
          </section>
          <section className="grammar-unlock-note" data-testid="grammar-unlock-note">
            <LockKeyhole size={20} /> Complete uma aula com 70% ou mais para marcar progresso. Você pode revisar e melhorar a pontuação quando quiser.
          </section>
        </>
      )}
    </div>
  );
}